import {
    trackEcommerce,
    trackEvent,
    trackingConstants,
    eventNameConstants,
    type EcomProduct,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';

import { graphql, readInlineData } from 'react-relay';

import { type personalizedCollectionsTracking_item$key } from './__generated__/personalizedCollectionsTracking_item.graphql.ts';

const { ECOM_PRODUCT_IMPRESSION, ECOM_PRODUCT_CLICK } = trackingConstants;
const { EVENT_VIEW_ITEM_LIST, EVENT_SELECT_ITEM, EVENT_PRODUCT_INTERACTION, EVENT_WISHLIST } =
    eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED } = stepInteractionConstants;

export const CATALOG_TYPES = {
    GLOBAL: 'GLOBAL',
    SPECIFIC: 'SPECIFIC',
    COMPLEMENTARY: 'COMPLEMENTARY',
};

export const CATALOG_TRIGGERS = {
    [CATALOG_TYPES.GLOBAL]: 'Global Catalog',
    [CATALOG_TYPES.SPECIFIC]: 'Specific Catalog',
    [CATALOG_TYPES.COMPLEMENTARY]: 'Complimentary Catalog',
};

export const CATALOG_LIST_TRIGGERS = {
    [CATALOG_TYPES.GLOBAL]: 'Global Catalog Landing',
    [CATALOG_TYPES.SPECIFIC]: 'Specific Catalog Landing',
    [CATALOG_TYPES.COMPLEMENTARY]: 'Complimentary Catalog Landing',
};

export type CatalogTrigger = (typeof CATALOG_TRIGGERS)[keyof typeof CATALOG_TRIGGERS];

type TrackCatalogImpressionParams = {
    products: EcomProduct[];
    trigger: CatalogTrigger;
    catalogID?: string | null;
};

export const trackCatalogModuleImpression = ({
    products,
    trigger,
}: TrackCatalogImpressionParams): void => {
    trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        trigger,
        products,
        isInteractiveEvent: false,
    });
};

type TrackCatalogProductClickParams = {
    product: EcomProduct | null;
    trigger: CatalogTrigger;
    itemId?: string | null;
    catalogID?: string | null;
};

export const trackCatalogProductClick = ({
    product,
    trigger,
    itemId,
}: TrackCatalogProductClickParams): void => {
    if (!product) {
        return;
    }
    trackEcommerce({
        type: ECOM_PRODUCT_CLICK,
        eventName: EVENT_SELECT_ITEM,
        trigger,
        actionField: {
            list: trigger,
            itemId: itemId || product.id || null,
        },
        products: [product],
        isInteractiveEvent: true,
    });
};

type TrackViewCollectionClickParams = {
    trigger: CatalogTrigger;
    catalogID?: string | null;
};

export const trackViewCollectionClick = ({
    trigger,
    catalogID,
}: TrackViewCollectionClickParams): void => {
    trackEvent({
        eventName: EVENT_PRODUCT_INTERACTION,
        interaction_type: 'view collection clicked',
        trigger,
        isInteractiveEvent: true,
        catalogID: catalogID || null,
    });
};

type TrackFavoritesClickParams = {
    trigger: CatalogTrigger;
    catalogID?: string | null;
};

export const trackFavoritesClick = ({ trigger, catalogID }: TrackFavoritesClickParams): void => {
    trackEvent({
        eventName: EVENT_WISHLIST,
        interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
        step_interaction_name: STEP_INTERACTION_ITEM_ADDED,
        trigger,
        isInteractiveEvent: true,
        catalogID: catalogID || null,
    });
};

export const getTriggerFromCatalogType = (
    catalogType: string | null | undefined,
    isList: boolean = false,
    positionIndex?: number
): CatalogTrigger => {
    const triggerDictionary = isList ? CATALOG_LIST_TRIGGERS : CATALOG_TRIGGERS;

    if (!catalogType) {
        return triggerDictionary[CATALOG_TYPES.GLOBAL];
    }

    let trigger = triggerDictionary[catalogType as keyof typeof triggerDictionary];
    if (typeof positionIndex === 'number') {
        trigger = `${trigger} | Position ${positionIndex + 1}`;
    }

    return trigger;
};

export const convertItemToEcomProduct = (
    itemRef: personalizedCollectionsTracking_item$key,
    index: number,
    trigger: CatalogTrigger,
    catalogID?: string | null
): (EcomProduct & { catalogID?: string | null }) | null => {
    const item = readInlineData(
        graphql`
            fragment personalizedCollectionsTracking_item on Item @inline {
                serviceId
                title
                browseUrl
                categoryCode
                seller {
                    serviceId
                }
                contemporaryTrackingString
                localizedPdpUrl
            }
        `,
        itemRef
    );

    if (!item || !item.serviceId) {
        return null;
    }

    return {
        id: item.serviceId,
        name: item.title || '',
        category: item.browseUrl || item.categoryCode || '',
        categoryCode: item.categoryCode || '',
        brand: item.seller?.serviceId || '',
        url: item.localizedPdpUrl || '',
        position: index + 1,
        list: trigger,
        dimension83: item.contemporaryTrackingString || '',
        catalogID: catalogID || null,
    };
};
